import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import styles from './LoadingScreen.module.css';

// Star logo path (из White_Logo_Star_without_box.svg)
const STAR_PATH = 'M93.4935 91.3206C91.7682 96.516 89.7087 101.493 87.2263 106.413C79.6467 121.44 69.2151 134.668 55.392 145.736C46.0271 153.235 35.9686 158.944 25.4152 163.351C18.0189 166.44 10.5867 168.784 3.13108 170.587C2.91743 170.639 2.7055 170.669 2.37933 170.731C2.60031 169.824 2.7865 168.956 3.02887 168.087C6.12976 156.968 10.9863 146.135 17.764 135.652C30.9818 115.21 48.6996 100.556 70.6255 91.3859C75.9636 89.1536 81.3298 87.365 86.719 85.9346C86.8865 85.8902 87.0539 85.82 87.3677 85.7114C85.5896 85.6235 83.9351 85.5857 82.3141 85.4558C63.681 83.9714 48.9651 77.0769 39.2709 63.2989C34.2765 56.1998 31.3256 47.8812 29.7978 38.7327C28.7543 32.476 28.4358 25.9741 28.6412 19.2971C28.6422 19.259 28.6652 19.2159 28.6942 19.128C29.3582 19.054 30.0387 18.945 30.7004 18.9093C44.7309 18.1448 57.4072 20.3004 68.4228 25.9466C82.2429 33.0306 90.5481 44.6729 93.9318 60.3301C95.1857 66.13 95.6657 72.2114 95.601 78.4921C95.6005 78.6002 95.6129 78.7056 95.7207 78.793C96.201 77.4862 96.6546 76.1763 97.1655 74.8723C104.271 56.7684 115.295 40.6042 131.391 26.9926C140.683 19.1336 150.782 13.1279 161.449 8.50105C169.902 4.83401 178.404 2.1596 186.937 0.250004C187.281 0.173644 187.623 0.098679 187.967 0.0297008C188.025 0.0183193 188.08 0.0403655 188.218 0.0557242C187.923 1.13808 187.656 2.22345 187.326 3.30726C184.561 12.3806 180.591 21.2617 175.383 29.9323C166.648 44.474 155.353 56.9038 140.922 66.7089C131.676 72.9903 121.995 77.5883 112.003 80.8622C108.632 81.9669 105.254 82.8822 101.872 83.8851C102.757 83.9423 103.67 83.9903 104.575 84.0613C118.646 85.1716 130.717 89.4641 140.224 97.8045C147.795 104.448 152.737 113.037 155.519 123.202C157.535 130.566 158.335 138.399 158.211 146.582C158.18 148.636 158.09 150.705 158.024 152.866C157.193 152.915 156.462 152.982 155.745 152.998C141.615 153.312 128.98 150.635 118.183 144.375C105.182 136.837 97.5595 125.068 94.715 109.596C93.6721 103.929 93.3326 98.0184 93.555 91.9165C93.5602 91.7637 93.5262 91.6214 93.4945 91.3218L93.4935 91.3206Z';

interface LoadingScreenProps {
  onLoadingComplete?: () => void;
}

type Phase = 'init' | 'draw' | 'fill' | 'zoom';

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ onLoadingComplete }) => {
  const { t } = useTranslation('common');
  const { haptic, user } = useTelegramWebApp();
  const pathRef = useRef<SVGPathElement>(null);

  const [phase, setPhase] = useState<Phase>('init');
  const [showGreeting, setShowGreeting] = useState(false);
  const [pathLength, setPathLength] = useState(0);

  const userName = user?.first_name || t('loadingScreen.user');

  // Проверяем prefers-reduced-motion
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Измеряем реальную длину path при монтировании
  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
      requestAnimationFrame(() => setPhase('draw'));
    }
  }, []);

  useEffect(() => {
    if (phase !== 'draw') return;

    // Для reduced-motion — сразу показываем всё и уходим
    if (prefersReducedMotion) {
      setShowGreeting(true);
      const timer = setTimeout(() => onLoadingComplete?.(), 800);
      return () => clearTimeout(timer);
    }

    haptic.impact('light');

    // 1600ms — stroke завершён, начинаем fill + glow
    setTimeout(() => {
      setPhase('fill');
      haptic.impact('medium');
    }, 1600);

    // 1900ms — приветствие
    setTimeout(() => {
      setShowGreeting(true);
      haptic.impact('light');
    }, 1900);

    // 2600ms — zoom out
    setTimeout(() => {
      setPhase('zoom');
      haptic.impact('heavy');
    }, 2600);

    // 3400ms — done
    setTimeout(() => {
      onLoadingComplete?.();
    }, 3400);
  }, [phase]);

  const pathClass = [
    styles.logoPath,
    phase === 'draw' ? styles.drawing : '',
    phase === 'fill' ? styles.filled : '',
    phase === 'zoom' ? styles.zooming : '',
    prefersReducedMotion ? styles.reducedMotion : '',
  ].filter(Boolean).join(' ');

  const screenClass = [
    styles.loadingScreen,
    phase === 'zoom' ? styles.zoomOut : '',
    prefersReducedMotion ? styles.reducedMotion : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={screenClass}>
      <div className={`${styles.logoContainer} ${phase === 'fill' || phase === 'zoom' ? styles.logoBreathing : ''}`}>
        {/* Ambient glow — появляется на fill-фазе */}
        <div className={`${styles.glowEffect} ${phase === 'fill' || phase === 'zoom' ? styles.glowVisible : ''}`} />

        {/* Star Logo SVG */}
        <svg
          className={styles.logoSvg}
          viewBox="0 0 189 171"
          overflow="visible"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Clip path по форме звезды — shimmer будет виден только внутри */}
            <clipPath id="starClip">
              <path d={STAR_PATH} />
            </clipPath>
            {/* Градиент для shimmer — широкая мягкая полоса */}
            <linearGradient id="shimmerGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="20%" stopColor="white" stopOpacity="0" />
              <stop offset="40%" stopColor="white" stopOpacity="0.2" />
              <stop offset="50%" stopColor="white" stopOpacity="0.4" />
              <stop offset="60%" stopColor="white" stopOpacity="0.2" />
              <stop offset="80%" stopColor="white" stopOpacity="0" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>

          <path
            ref={pathRef}
            d={STAR_PATH}
            className={pathClass}
            style={pathLength ? {
              '--path-length': pathLength,
              '--path-75': pathLength * 0.75,
              '--path-50': pathLength * 0.5,
              '--path-25': pathLength * 0.25,
            } as React.CSSProperties : undefined}
          />

          {/* Shimmer — rect внутри clipped группы.
              clipPath на <g>, а не на rect, чтобы clip оставался
              на месте пока rect двигается через него */}
          <g clipPath="url(#starClip)">
            <rect
              x="-100"
              y="0"
              width="100"
              height="171"
              fill="url(#shimmerGrad)"
              className={`${styles.shimmerRect} ${phase === 'fill' || phase === 'zoom' ? styles.shimmerVisible : ''}`}
            />
          </g>
        </svg>

        {/* Приветствие */}
        <div className={`${styles.greeting} ${showGreeting ? styles.greetingVisible : ''}`}>
          {t('loadingScreen.hello', { name: userName })}
        </div>
      </div>
    </div>
  );
};
